import { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { InspectionTemplate, TemplateSection } from 'inspection-types';
import {
  useInspectionsTemplateCustomizeReorderEnabled,
  useReorderInspectionTemplate,
} from 'react-hooks';
import { hasPermissionOrSuperUser } from 'lane-shared/helpers/hasPermission';
import { PERMISSION_KEYS } from 'constants-permissions';

import UserDataContext from 'lane-shared/contexts/UserDataContext';

import {
  createTemplateReorderQueue,
  type TemplateReorderQueue,
} from '../../helpers/templateReorderQueue';

export type UseInspectionTemplateReorderParams = {
  channelId: string | undefined;
  templateId: string | undefined;
  template: InspectionTemplate | undefined;
  isReorderPending: boolean;
  isReorderBlocked: () => boolean;
  applyOptimistic: (next: InspectionTemplate) => void;
  settle: () => void;
  /** Releases one failed optimistic reorder without replacing newer pending optimistic state. */
  rollback: () => void;
  refetch: () => Promise<void>;
};

export type UseInspectionTemplateReorderResult = {
  reorderEnabled: boolean;
  reorderSection: (activeId: string, overId: string) => Promise<void>;
  reorderItemWithinSection: (
    sectionId: string,
    activeId: string,
    overId: string
  ) => Promise<void>;
};

function moveById<T extends { id: string }>(
  list: readonly T[],
  activeId: string,
  overId: string
): T[] | null {
  const fromIndex = list.findIndex(item => item.id === activeId);
  const toIndex = list.findIndex(item => item.id === overId);

  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return null;
  }

  const next = list.slice();
  const [moved] = next.splice(fromIndex, 1);

  next.splice(toIndex, 0, moved);

  return next;
}

function reorderTemplateSections(
  template: InspectionTemplate,
  orderedSections: TemplateSection[]
): InspectionTemplate {
  return {
    ...template,
    sections: orderedSections.map((section, index) => ({
      ...section,
      order: index,
    })),
  };
}

function reorderTemplateItems(
  template: InspectionTemplate,
  sectionId: string,
  orderedSection: TemplateSection
): InspectionTemplate {
  return {
    ...template,
    sections: template.sections.map(section =>
      section.id === sectionId ? orderedSection : section
    ),
  };
}

function buildSectionOrderedItems(
  section: TemplateSection,
  activeId: string,
  overId: string
): TemplateSection | null {
  const reorderedItems = moveById(section.items, activeId, overId);

  if (!reorderedItems) {
    return null;
  }

  return {
    ...section,
    items: reorderedItems.map((item, index) => ({ ...item, order: index })),
  };
}

export function useInspectionTemplateReorder({
  channelId,
  templateId,
  template,
  isReorderPending,
  isReorderBlocked,
  applyOptimistic,
  settle,
  rollback,
  refetch,
}: UseInspectionTemplateReorderParams): UseInspectionTemplateReorderResult {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const flagEnabled = useInspectionsTemplateCustomizeReorderEnabled();
  const { reorderInspectionTemplate } = useReorderInspectionTemplate();

  const queueRef = useRef<TemplateReorderQueue | null>(null);

  if (!queueRef.current) {
    queueRef.current = createTemplateReorderQueue();
  }
  const latestTemplateRef = useRef<InspectionTemplate | undefined>(template);
  const lastSuccessfulTemplateRef = useRef<InspectionTemplate | undefined>(
    template
  );

  if (!isReorderPending) {
    latestTemplateRef.current = template;
    lastSuccessfulTemplateRef.current = template;
  }

  const hasReorderPermission = useMemo(() => {
    if (!user || !channelId) {
      return false;
    }

    return hasPermissionOrSuperUser(
      user,
      [PERMISSION_KEYS.PERMISSION_INSPECTIONS_TEMPLATES_REORDER],
      channelId
    );
  }, [user, channelId]);

  const reorderEnabled = flagEnabled && hasReorderPermission;

  const showErrorToast = useCallback(async () => {
    await window.Toast.show(
      t('web.admin.inspections.templateCustomize.reorderErrorToast')
    );
  }, [t]);

  const runReorder = useCallback(
    async (
      nextTemplate: InspectionTemplate,
      payload: {
        sectionIds: string[];
        sections: { sectionId: string; itemIds: string[] }[];
      }
    ): Promise<void> => {
      if (
        !reorderEnabled ||
        !channelId ||
        !templateId ||
        isReorderBlocked()
      ) {
        return;
      }

      latestTemplateRef.current = nextTemplate;
      applyOptimistic(nextTemplate);

      try {
        await queueRef.current!.enqueueByKey(templateId, async () => {
          await reorderInspectionTemplate({
            channelId,
            templateId,
            sectionIds: payload.sectionIds,
            sections: payload.sections,
          });
          lastSuccessfulTemplateRef.current = nextTemplate;
          await refetch();
        });
        settle();
      } catch (error) {
        latestTemplateRef.current = lastSuccessfulTemplateRef.current;
        try {
          await refetch();
        } finally {
          rollback();
        }
        await showErrorToast();
        throw error;
      }
    },
    [
      applyOptimistic,
      channelId,
      isReorderBlocked,
      refetch,
      reorderEnabled,
      reorderInspectionTemplate,
      rollback,
      settle,
      showErrorToast,
      templateId,
    ]
  );

  const reorderSection = useCallback(
    async (activeId: string, overId: string): Promise<void> => {
      const currentTemplate = latestTemplateRef.current;

      if (!reorderEnabled || !currentTemplate) {
        return;
      }

      const orderedSections = [...currentTemplate.sections].sort(
        (a, b) => a.order - b.order
      );
      const reordered = moveById(orderedSections, activeId, overId);

      if (!reordered) {
        return;
      }

      const nextTemplate = reorderTemplateSections(currentTemplate, reordered);

      await runReorder(nextTemplate, {
        sectionIds: reordered.map(section => section.id),
        sections: [],
      });
    },
    [reorderEnabled, runReorder]
  );

  const reorderItemWithinSection = useCallback(
    async (
      sectionId: string,
      activeId: string,
      overId: string
    ): Promise<void> => {
      const currentTemplate = latestTemplateRef.current;

      if (!reorderEnabled || !currentTemplate) {
        return;
      }

      const targetSection = currentTemplate.sections.find(
        section => section.id === sectionId
      );

      if (!targetSection) {
        return;
      }

      const orderedItems = [...targetSection.items].sort(
        (a, b) => a.order - b.order
      );
      const reorderedSection = buildSectionOrderedItems(
        { ...targetSection, items: orderedItems },
        activeId,
        overId
      );

      if (!reorderedSection) {
        return;
      }

      const nextTemplate = reorderTemplateItems(
        currentTemplate,
        sectionId,
        reorderedSection
      );

      await runReorder(nextTemplate, {
        sectionIds: [],
        sections: [
          {
            sectionId,
            itemIds: reorderedSection.items.map(item => item.id),
          },
        ],
      });
    },
    [reorderEnabled, runReorder]
  );

  return {
    reorderEnabled,
    reorderSection,
    reorderItemWithinSection,
  };
}
